import { LogoIcon, SearchIcon } from '@/components/base/Icons/RocheIconPack';
import { WhiteHeartIcon } from '@/components/base/Icons/WhiteHeartIcon';
import { useMyHCPsNavigation } from '@/components/hcp/HCPList/hooks/useMyHCPsNavigation';
import { useAuthContext } from '@/context/AuthContext';
import { useFiltersContext } from '@/context/FiltersContext/FiltersContextProvider';
import { useUserProfile } from '@/hooks';
import { useCentralizedLinkClick } from '@/hooks/AdobeAnalyticsHooks/linkClick/useCentralizedLinkClick';
import IconButton from '@/storybook/components/IconButton/IconButton';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import AppLauncher from '../AppLauncher/AppLauncher';
import UserMenu from '../UserMenu/UserMenu';
import styles from './ApplicationHeader.module.css';
import { TestIds } from './testids';

const ApplicationHeader = () => {
  const { t } = useTranslation();
  const user = useUserProfile();
  const { isLoggedIn } = useAuthContext();
  const { setFilterOverlayVisible } = useFiltersContext();
  const { navigateToFavorites } = useMyHCPsNavigation();
  const { trackSearchClick, trackFavoritesClick } = useCentralizedLinkClick();
  const showSearchGlasses = user?.disclaimerRead;

  const handleCloseFilterOverlay = useCallback(() => {
    setFilterOverlayVisible(false);
  }, [setFilterOverlayVisible]);

  const handleLogoClick = useCallback(() => {
    handleCloseFilterOverlay();
  }, [handleCloseFilterOverlay]);

  const handleSearchClick = useCallback(() => {
    handleCloseFilterOverlay();
    trackSearchClick({
      innertext: t('global.search'),
    });
  }, [handleCloseFilterOverlay, trackSearchClick, t]);

  const handleFavoritesClick = useCallback(() => {
    handleCloseFilterOverlay();
    trackFavoritesClick();
    navigateToFavorites();
  }, [handleCloseFilterOverlay, trackFavoritesClick, navigateToFavorites]);

  return (
    <div className={styles.headerContainer}>
      <div className={styles.headerBackground}>
        <div className={styles.headerLeft}>
          <RouterLink
            to={user.userHomeRoute}
            className={styles.iconLogo}
            data-testid={TestIds.topBar.rocheLogo}
            onClick={handleLogoClick}
          >
            <LogoIcon width="62" className={styles.iconFill} />
          </RouterLink>
          <div className={styles.whiteSeparationLine}>|</div>
          <div className={styles.headerText}>Customer Engagement Suite</div>
        </div>
        {isLoggedIn && (
          <div className={styles.headerRight}>
            {showSearchGlasses && (
              <RouterLink
                to="/search-hcps"
                className={styles.iconSearch}
                data-testid={TestIds.topBar.search}
                onClick={handleSearchClick}
              >
                <SearchIcon fill="#544F4F" height="2.4rem" />
                <p className={styles.searchText}>{t('global.search')}</p>
              </RouterLink>
            )}
            <IconButton
              theme="dark"
              size="M"
              data-testid={TestIds.topBar.favorite}
              appearance="text"
              icon={<WhiteHeartIcon />}
              onClick={handleFavoritesClick}
            />
            <AppLauncher onOpen={handleCloseFilterOverlay} />
            <UserMenu onOpen={handleCloseFilterOverlay} />
          </div>
        )}
      </div>
    </div>
  );
};

export default ApplicationHeader;
